import React, { useEffect, useState } from 'react';
import {
  Card, Button, Input, Table, Tag, Statistic, Row, Col, Typography, Alert,
  Popconfirm, theme,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  CloudOutlined, SaveOutlined, DeleteOutlined, CheckCircleOutlined,
  HistoryOutlined, SlidersOutlined, SafetyCertificateOutlined,
} from '@ant-design/icons';
import { calculateEbtronCalibration } from './ebtronCalculations';
import { saveEbtronResult, getEbtronResults, deleteEbtronResult } from '../../firebase/firestore';
import { EbtronResult } from '../../models/types';

interface Props { projectId: string; }

const emptyForm = () => ({
  sensorLabel: '', minDisplayed: '', minActual: '', maxDisplayed: '', maxActual: '',
  ductArea: '', midrangeActual: '', notes: '',
});

const EbtronModule: React.FC<Props> = ({ projectId }) => {
  const { token } = theme.useToken();
  const [form, setForm] = useState(emptyForm());
  const [results, setResults] = useState<EbtronResult[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getEbtronResults(projectId)
      .then(setResults).catch(() => setError('Failed to load results')).finally(() => setLoading(false));
  }, [projectId]);

  const n = (v: string) => parseFloat(v) || 0;

  const calc = calculateEbtronCalibration({
    minDisplayed: n(form.minDisplayed), minActual: n(form.minActual),
    maxDisplayed: n(form.maxDisplayed), maxActual: n(form.maxActual),
    ductArea: n(form.ductArea),
    midrangeActual: form.midrangeActual !== '' ? n(form.midrangeActual) : undefined, // fix B-4: 0 was falsy
  });

  const handleSave = async () => {
    if (!calc) return;
    setSaving(true); setError('');
    try {
      const result: Omit<EbtronResult, 'id'> = {
        projectId,
        sensorLabel: form.sensorLabel,
        minDisplayed: n(form.minDisplayed), minActual: n(form.minActual),
        maxDisplayed: n(form.maxDisplayed), maxActual: n(form.maxActual),
        ductArea: n(form.ductArea),
        gain: calc.gain, offsetCfm: calc.offsetCfm, offsetFpm: calc.offsetFpm,
        midrangeDisplayed: calc.midrangeDisplayed,
        midrangeActual: form.midrangeActual !== '' ? n(form.midrangeActual) : undefined, // fix B-4: 0 was falsy
        midrangeAccuracyPct: calc.midrangeAccuracyPct,
        midrangePass: calc.midrangePass,
        notes: form.notes,
        createdAt: new Date().toISOString(),
      };
      const id = await saveEbtronResult(result);
      setResults(prev => [{ ...result, id }, ...prev]);
      setForm(emptyForm());
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 1500);
    } catch (err: any) { setError(`Failed to save: ${err?.message ?? err?.code ?? 'Unknown error'}`); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    try { await deleteEbtronResult(projectId, id); setResults(prev => prev.filter(r => r.id !== id)); }
    catch { setError('Failed to delete'); }
  };

  const field = (label: string, key: keyof ReturnType<typeof emptyForm>, type = 'number', placeholder = '') => (
    <div>
      <div style={{ fontSize: 13, marginBottom: 6 }}>{label}</div>
      <Input
        type={type} placeholder={placeholder}
        value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
      />
    </div>
  );

  const sectionLabel = (text: string) => (
    <Typography.Text type="secondary" strong style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '.5px', display: 'block', margin: '18px 0 10px' }}>
      {text}
    </Typography.Text>
  );

  const grid2: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 };

  const savedColumns: ColumnsType<EbtronResult> = [
    { title: 'Sensor', dataIndex: 'sensorLabel', render: v => v || <Typography.Text type="secondary">—</Typography.Text> },
    { title: 'Gain', dataIndex: 'gain', align: 'right', render: (v: number) => v.toFixed(4) },
    { title: 'Offset CFM', dataIndex: 'offsetCfm', align: 'right', render: (v: number) => v.toFixed(2) },
    { title: 'Offset FPM', dataIndex: 'offsetFpm', align: 'right', render: (v: number) => v.toFixed(2) },
    {
      title: 'Midrange %', dataIndex: 'midrangeAccuracyPct', align: 'right',
      render: (v: number | undefined) => v != null ? `${v.toFixed(2)}%` : '—',
    },
    {
      title: 'Pass?', dataIndex: 'midrangePass', width: 90,
      render: (v: boolean | undefined) => v != null
        ? <Tag color={v ? 'green' : 'red'} style={{ marginInlineEnd: 0 }}>{v ? 'Pass' : 'Fail'}</Tag>
        : '—',
    },
    { title: 'Date', dataIndex: 'createdAt', width: 120, render: v => new Date(v).toLocaleDateString() },
    {
      title: '', key: 'actions', width: 50, align: 'right',
      render: (_, r) => (
        <Popconfirm title="Delete this calibration result?" onConfirm={() => handleDelete(r.id!)} okText="Delete" okButtonProps={{ danger: true }}>
          <Button type="text" size="small" danger icon={<DeleteOutlined />} aria-label="Delete calibration" />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      {/* Page head */}
      <Card style={{ marginBottom: 16 }} styles={{ body: { padding: '16px 24px' } }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <div>
            <Typography.Title level={4} style={{ margin: 0 }}>
              <CloudOutlined style={{ color: token.colorPrimary, marginRight: 8 }} />Ebtron Airflow
            </Typography.Title>
            <Typography.Text type="secondary">Flow sensor calibration</Typography.Text>
          </div>
        </div>
      </Card>

      {error && <Alert type="error" showIcon message={error} closable onClose={() => setError('')} style={{ marginBottom: 16 }} />}

      <Row gutter={[16, 16]}>
        {/* LEFT: calibration form */}
        <Col xs={24} lg={10}>
          <Card title={<span><SlidersOutlined /> New Calibration</span>}>
            <div style={grid2}>
              {field('Sensor Label', 'sensorLabel', 'text', 'e.g. AHU-1 Supply')}
              <div>
                <div style={{ fontSize: 13, marginBottom: 6 }}>Duct Area (ft²)</div>
                <Input type="number" value={form.ductArea} onChange={e => setForm(f => ({ ...f, ductArea: e.target.value }))} />
                {form.ductArea === '' && (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>Required — duct cross-section area in ft²</Typography.Text>
                )}
              </div>
            </div>
            <div style={{ marginTop: 14 }}>
              {field('Notes', 'notes', 'text', 'Optional')}
            </div>

            {sectionLabel('Measurement Points')}
            <div style={{ ...grid2, marginBottom: 14 }}>
              {field('Min Displayed (CFM)', 'minDisplayed')}
              {field('Min Actual (CFM)', 'minActual')}
            </div>
            <div style={grid2}>
              {field('Max Displayed (CFM)', 'maxDisplayed')}
              {field('Max Actual (CFM)', 'maxActual')}
            </div>

            {sectionLabel('Midrange Check (Optional)')}
            <div style={grid2}>
              <div>
                <div style={{ fontSize: 13, marginBottom: 6 }}>Midrange Displayed (auto)</div>
                <Input readOnly style={{ background: token.colorFillTertiary }} value={calc ? calc.midrangeDisplayed.toFixed(2) : '—'} />
              </div>
              {field('Midrange Actual (CFM)', 'midrangeActual')}
            </div>

            <div style={{ marginTop: 18, display: 'flex', alignItems: 'center', gap: 10 }}>
              <Button type="primary" icon={<SaveOutlined />} loading={saving} disabled={!calc || saving} onClick={handleSave}>
                Save calibration
              </Button>
              {saveSuccess && (
                <Typography.Text style={{ color: '#389e0d' }}><CheckCircleOutlined /> Saved</Typography.Text>
              )}
            </div>
          </Card>
        </Col>

        {/* RIGHT: results */}
        <Col xs={24} lg={14}>
          {calc ? (
            <>
              {calc.midrangeAccuracyPct !== undefined && (
                <Alert
                  type={calc.midrangePass ? 'success' : 'error'}
                  showIcon
                  style={{ marginBottom: 16 }}
                  message={<b>Midrange accuracy check — {calc.midrangePass ? 'PASS' : 'FAIL'}</b>}
                  description={`${calc.midrangeAccuracyPct.toFixed(2)}% accuracy`}
                />
              )}
              <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col flex="1 1 0">
                  <Card styles={{ body: { padding: '16px 20px' } }}>
                    <Statistic title="Gain" value={calc.gain.toFixed(4)} valueStyle={{ fontSize: 20 }} />
                  </Card>
                </Col>
                <Col flex="1 1 0">
                  <Card styles={{ body: { padding: '16px 20px' } }}>
                    <Statistic title="Offset (CFM)" value={calc.offsetCfm.toFixed(2)} valueStyle={{ fontSize: 20 }} />
                  </Card>
                </Col>
                <Col flex="1 1 0">
                  <Card styles={{ body: { padding: '16px 20px' } }}>
                    <Statistic title="Offset (FPM)" value={calc.offsetFpm.toFixed(2)} valueStyle={{ fontSize: 20 }} />
                  </Card>
                </Col>
              </Row>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                <SafetyCertificateOutlined /> Calculated with Sustainability-Pros' proprietary airflow engine
              </Typography.Text>
            </>
          ) : (
            <Alert type="info" showIcon message="Enter the min and max measurement points (and duct area) to compute the calibration." />
          )}
        </Col>
      </Row>

      {/* Saved calibrations */}
      <Card
        title={<span><HistoryOutlined /> Saved Calibrations</span>}
        style={{ marginTop: 16 }}
        styles={{ body: { padding: 0 } }}
      >
        <Table<EbtronResult>
          rowKey="id"
          size="middle"
          loading={loading}
          columns={savedColumns}
          dataSource={results}
          pagination={results.length > 20 ? { pageSize: 20 } : false}
          locale={{ emptyText: 'No calibrations saved yet.' }}
        />
      </Card>
    </div>
  );
};

export default EbtronModule;
